import { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import {
  CheckCheck,
  Inbox,
  KeyRound,
  Loader2,
  Plug,
  RefreshCw,
  Settings2,
  Trash2
} from "lucide-react";

import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import useQueues from "../../hooks/useQueues";
import { cn } from "../../lib/utils";
import { COM_INBOX, identidadeDaRede, nomeDaRede } from "./redes";
import Cabecalho from "./Cabecalho";
import ContaModal from "./ContaModal";

// As redes que dá para autorizar por aqui. É o recorte de quem recebe mensagem
// direta — conectar uma rede sem inbox só encheria a lista de conta que nunca
// traz conversa.
const REDES_CONECTAVEIS = [
  "instagram",
  "facebook",
  "telegram",
  "twitter",
  "reddit",
  "bluesky",
  "discord"
];

// Contas da Zernio no atendimento.
//
// A tela responde duas perguntas: quem já atende aqui, e o que existe na
// Zernio esperando ser trazido. Cada conta conectada vira uma conexão, e a
// conversa dela cai em Tickets junto com as do WhatsApp.

// "há 3 min" diz mais que um horário: o que se quer saber é se a lista está
// velha, não que horas eram quando ela foi buscada.
const desdeQuando = (data) => {
  if (!data) return null;

  const minutos = Math.floor((Date.now() - new Date(data).getTime()) / 60000);

  if (minutos < 1) return "agora";
  if (minutos < 60) return `há ${minutos} min`;

  const horas = Math.floor(minutos / 60);

  if (horas < 24) return `há ${horas} h`;

  return `há ${Math.floor(horas / 24)} d`;
};

const ultimaSincronizacao = (contas) => {
  const datas = contas.map((conta) => conta.syncedAt).filter(Boolean);

  if (datas.length === 0) return "nunca";

  return desdeQuando(datas.sort().reverse()[0]);
};

// Tela vazia é convite para agir, não aviso de erro: o ícone e a frase dizem
// o que fazer em seguida.
const VazioComIcone = ({ icone: Icone, titulo, texto }) => (
  <div className="flex flex-col items-center justify-center rounded-xl border border-dashed px-6 py-10 text-center">
    <span className="flex h-10 w-10 items-center justify-center rounded-full bg-muted text-muted-foreground">
      <Icone className="h-5 w-5" />
    </span>
    <p className="mt-3 text-sm font-medium">{titulo}</p>
    <p className="mt-1 max-w-sm text-sm text-muted-foreground">{texto}</p>
  </div>
);

// O cartão de uma conta que já atende.
//
// A cor da marca aparece em dois lugares e só neles — a faixa da lateral e o
// glifo. É o que deixa varrer a lista e achar "a do Instagram" sem ler nome
// nenhum; espalhar a mesma cor pelo resto do cartão só faria borrão.
const CartaoConta = ({
  conta,
  filas,
  sincronizando,
  bloqueado,
  onSincronizar,
  onEditar,
  onDesconectar
}) => {
  const rede = identidadeDaRede(conta.platform);
  const Glifo = rede.icone;
  const fila = filas.find((item) => item.id === conta.queueId);

  return (
    <div
      className={cn(
        "relative overflow-hidden rounded-xl border bg-card p-4 transition-colors hover:border-foreground/20",
        !conta.isActive && "opacity-60"
      )}
    >
      <span
        aria-hidden
        className="absolute inset-y-0 left-0 w-1"
        style={{ backgroundColor: rede.corSelo }}
      />

      <div className="flex items-start gap-3 pl-2">
        <span
          className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl text-white"
          style={{ backgroundColor: rede.corSelo }}
        >
          <Glifo className="h-5 w-5" />
        </span>

        <div className="min-w-0 flex-1">
          <p className="truncate font-medium leading-tight">{conta.name}</p>
          <p className="mt-0.5 truncate text-xs text-muted-foreground">
            {rede.rotulo}
            {conta.username ? ` · ${conta.username}` : ""}
          </p>
        </div>

        {!conta.isActive && (
          <Badge variant="secondary" className="shrink-0">
            Pausada
          </Badge>
        )}
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1 pl-2 text-xs text-muted-foreground">
        <span className="inline-flex items-center gap-1">
          <Inbox className="h-3 w-3" />
          {fila?.name || "Sem fila"}
        </span>
        <span className="inline-flex items-center gap-1">
          <RefreshCw className="h-3 w-3" />
          {conta.syncedAt ? desdeQuando(conta.syncedAt) : "nunca sincronizada"}
        </span>
      </div>

      <div className="mt-3 flex items-center gap-1 border-t pl-2 pt-3">
        <Button
          variant="ghost"
          size="sm"
          onClick={onSincronizar}
          disabled={bloqueado}
        >
          {sincronizando ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <RefreshCw className="h-4 w-4" />
          )}
          Buscar conversas
        </Button>

        <Button
          variant="ghost"
          size="icon"
          className="ml-auto"
          onClick={onEditar}
          aria-label="Ajustar conta"
        >
          <Settings2 className="h-4 w-4" />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          onClick={onDesconectar}
          aria-label="Desconectar conta"
        >
          <Trash2 className="h-4 w-4 text-destructive" />
        </Button>
      </div>
    </div>
  );
};

// O cartão de uma conta que existe na Zernio e ainda não atende aqui. Mais
// quieto de propósito: o glifo sai em cinza porque a conta ainda não é nossa.
const CartaoDisponivel = ({ conta, onConectar }) => {
  const rede = identidadeDaRede(conta.platform);
  const Glifo = rede.icone;
  const conversa = COM_INBOX.includes(conta.platform);

  return (
    <div className="flex items-center gap-3 rounded-xl border border-dashed bg-card/50 p-4">
      <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-muted text-muted-foreground">
        <Glifo className="h-5 w-5" />
      </span>

      <div className="min-w-0 flex-1">
        <p className="truncate font-medium leading-tight">{conta.name}</p>
        <p className="mt-0.5 truncate text-xs text-muted-foreground">
          {rede.rotulo}
        </p>
        {!conversa && (
          <p className="mt-1 text-xs text-amber-600 dark:text-amber-400">
            Esta rede não recebe mensagem direta
          </p>
        )}
      </div>

      <Button size="sm" onClick={onConectar}>
        <Plug className="h-4 w-4" />
        Conectar
      </Button>
    </div>
  );
};

const ZernioContas = () => {
  const [status, setStatus] = useState(null);
  const [conectadas, setConectadas] = useState([]);
  const [disponiveis, setDisponiveis] = useState([]);
  const [filas, setFilas] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [sincronizando, setSincronizando] = useState(null);
  const [editando, setEditando] = useState(null);
  const [conectandoRede, setConectandoRede] = useState(null);

  const { findAll: listarFilas } = useQueues();

  const carregarStatus = useCallback(async () => {
    try {
      const { data } = await api.get("/zernio/status");
      setStatus(data);
    } catch (err) {
      toastError(err);
    }
  }, []);

  const carregarContas = useCallback(async () => {
    try {
      const { data } = await api.get("/zernio/accounts");
      setConectadas(data);
    } catch (err) {
      toastError(err);
    }
  }, []);

  // Só faz sentido pedir à Zernio a lista de contas quando há chave: sem ela a
  // chamada volta erro e a tela mostraria um alerta que não ajuda ninguém.
  const carregarDisponiveis = useCallback(async () => {
    try {
      const { data } = await api.get("/zernio/accounts/available");
      setDisponiveis(data);
    } catch (err) {
      toastError(err);
    }
  }, []);

  useEffect(() => {
    (async () => {
      await carregarStatus();
      await carregarContas();

      try {
        setFilas(await listarFilas());
      } catch (err) {
        toastError(err);
      }

      setCarregando(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (status?.configurada) carregarDisponiveis();
  }, [status?.configurada, carregarDisponiveis]);

  // Volta da autorização: a Zernio devolve com ?connected=<rede>. A conta já
  // foi criada pelo webhook, então aqui é só recarregar e avisar.
  useEffect(() => {
    const parametros = new URLSearchParams(window.location.search);
    const rede = parametros.get("connected");

    if (!rede) return;

    toast.success(`${nomeDaRede(rede)} conectado. Trazendo as conversas…`);
    carregarContas();
    carregarDisponiveis();

    // Limpa o endereço para um F5 não repetir o aviso.
    window.history.replaceState({}, "", window.location.pathname);
  }, [carregarContas, carregarDisponiveis]);

  const conectar = async (conta) => {
    try {
      await api.post("/zernio/accounts", {
        accountId: conta.accountId,
        platform: conta.platform,
        name: conta.name,
        username: conta.username,
        profileId: conta.profileId
      });

      toast.success(`${conta.name} conectada.`);
      await carregarContas();
      await carregarDisponiveis();
    } catch (err) {
      toastError(err);
    }
  };

  const alterar = async (conta, campos) => {
    try {
      await api.put(`/zernio/accounts/${conta.id}`, campos);
      await carregarContas();
    } catch (err) {
      toastError(err);
    }
  };

  const desconectar = async (conta) => {
    if (
      // eslint-disable-next-line no-alert
      !window.confirm(
        `Desconectar ${conta.name}? Os atendimentos já existentes continuam no histórico.`
      )
    ) {
      return;
    }

    try {
      await api.delete(`/zernio/accounts/${conta.id}`);
      toast.success("Conta desconectada.");
      await carregarContas();
      await carregarDisponiveis();
    } catch (err) {
      toastError(err);
    }
  };

  // Abre a autorização da rede numa aba. A Zernio devolve a pessoa para cá
  // com `?connected=`, e aí a lista é recarregada.
  const conectarRede = async (plataforma) => {
    setConectandoRede(plataforma);

    try {
      const { data } = await api.get(`/zernio/connect/${plataforma}`);

      window.open(data.authUrl, "_blank", "noopener");
      toast.info(
        "Autorize na aba que abriu. A conta entra aqui sozinha assim que você concluir."
      );
    } catch (err) {
      toastError(err);
    }

    setConectandoRede(null);
  };

  const sincronizar = async (conta) => {
    setSincronizando(conta ? conta.id : "todas");

    try {
      const { data } = await api.post(
        conta ? `/zernio/accounts/${conta.id}/sync` : "/zernio/accounts/sync"
      );

      toast.success(
        `${data.conversas} conversas e ${data.mensagens} mensagens trazidas.`
      );

      await carregarContas();
    } catch (err) {
      toastError(err);
    }

    setSincronizando(null);
  };

  if (carregando) {
    return (
      <div className="flex justify-center py-20">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const naoConectadas = disponiveis.filter((conta) => !conta.conectada);
  const ativas = conectadas.filter((conta) => conta.isActive);
  const redesAtivas = new Set(ativas.map((conta) => conta.platform));

  return (
    <div className="space-y-6 p-4">
      <Cabecalho
        titulo="Contas"
        descricao="Instagram, Telegram, Messenger, X, Reddit, Bluesky e Discord atendidos na mesma tela do WhatsApp."
        acao={
          conectadas.length > 0 && (
            <div className="flex items-center gap-5 rounded-xl border bg-card px-4 py-2.5">
              <div>
                <p className="text-xl font-semibold leading-none tabular-nums">
                  {ativas.length}
                </p>
                <p className="mt-1 text-[11px] uppercase tracking-wide text-muted-foreground">
                  contas ativas
                </p>
              </div>
              <div className="h-8 w-px bg-border" />
              <div>
                <p className="text-xl font-semibold leading-none tabular-nums">
                  {redesAtivas.size}
                </p>
                <p className="mt-1 text-[11px] uppercase tracking-wide text-muted-foreground">
                  redes
                </p>
              </div>
              <div className="h-8 w-px bg-border" />
              <div>
                <p className="text-sm font-medium leading-none">
                  {ultimaSincronizacao(conectadas)}
                </p>
                <p className="mt-1 text-[11px] uppercase tracking-wide text-muted-foreground">
                  última sincronia
                </p>
              </div>
            </div>
          )
        }
      />

      {!status?.configurada && (
        <div className="flex items-start gap-3 rounded-xl border border-amber-500/40 bg-amber-500/10 p-4 text-sm">
          <KeyRound className="mt-0.5 h-4 w-4 shrink-0 text-amber-600 dark:text-amber-400" />
          <p>
            Falta a chave da API. Sem ela nenhuma conta aparece aqui — informe
            em Redes sociais &rsaquo; Configuração.
          </p>
        </div>
      )}

      <section className="space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
            No atendimento
          </h2>
          <Button
            variant="outline"
            size="sm"
            onClick={() => sincronizar(null)}
            disabled={sincronizando !== null || !conectadas.length}
          >
            {sincronizando === "todas" ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4" />
            )}
            Buscar conversas novas
          </Button>
        </div>

        {conectadas.length === 0 ? (
          <VazioComIcone
            icone={Plug}
            titulo="Nenhuma conta no atendimento"
            texto="Conecte uma das contas abaixo para que as conversas dela cheguem em Tickets."
          />
        ) : (
          <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
            {conectadas.map((conta) => (
              <CartaoConta
                key={conta.id}
                conta={conta}
                filas={filas}
                sincronizando={sincronizando === conta.id}
                bloqueado={sincronizando !== null}
                onSincronizar={() => sincronizar(conta)}
                onEditar={() => setEditando(conta)}
                onDesconectar={() => desconectar(conta)}
              />
            ))}
          </div>
        )}
      </section>

      {/* Conectar uma rede é o único passo que não dá para automatizar: só o
          dono da conta pode autorizar, e ele faz isso no site da própria rede.
          Depois disso o resto corre sozinho — a Zernio avisa por webhook e a
          conta entra no atendimento com as conversas já sincronizadas. */}
      <section className="space-y-3">
        <div>
          <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
            Conectar uma rede
          </h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Você autoriza no site da rede e a conta entra aqui sozinha, já com
            as conversas.
          </p>
        </div>

        <div className="flex flex-wrap gap-2">
          {REDES_CONECTAVEIS.map((plataforma) => {
            const rede = identidadeDaRede(plataforma);
            const Glifo = rede.icone;

            return (
              <Button
                key={plataforma}
                variant="outline"
                onClick={() => conectarRede(plataforma)}
                disabled={conectandoRede !== null || !status?.configurada}
              >
                {conectandoRede === plataforma ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Glifo className="h-4 w-4" style={{ color: rede.corSelo }} />
                )}
                {rede.rotulo}
              </Button>
            );
          })}
        </div>
      </section>

      <section className="space-y-3">
        <div>
          <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
            Disponíveis na Zernio
          </h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Contas já conectadas no painel da Zernio que ainda não atendem aqui.
          </p>
        </div>

        {naoConectadas.length === 0 ? (
          <VazioComIcone
            icone={status?.configurada ? CheckCheck : KeyRound}
            titulo={
              status?.configurada
                ? "Tudo conectado"
                : "Chave da API não informada"
            }
            texto={
              status?.configurada
                ? "Todas as contas da Zernio já estão no atendimento."
                : "Informe a chave para listar as contas da Zernio."
            }
          />
        ) : (
          <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
            {naoConectadas.map((conta) => (
              <CartaoDisponivel
                key={conta.accountId}
                conta={conta}
                onConectar={() => conectar(conta)}
              />
            ))}
          </div>
        )}
      </section>

      <ContaModal
        open={Boolean(editando)}
        conta={editando}
        filas={filas}
        onClose={() => setEditando(null)}
        onSalvar={alterar}
      />
    </div>
  );
};

export default ZernioContas;
